import classNames from 'classnames';
import _ from 'lodash';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Virtuoso } from 'react-virtuoso';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { toHumanReadableDateDay } from '../../common/dateTimeHelper';
import ButtonSwitchMulti from '../../components/actions/ButtonSwitchMulti';
import { PanelBody } from '../../components/designer/panels/PanelBody';
import InboxZero from '../../components/InboxZero';
import TitleSection from '../../components/layout/TitleSection';
import { ActionBar } from '../../components/nav/ActionBar';
import FeedActivityEventDetails from '../components/FeedActivityEventDetails';
import {
  actualityEventsLimitAtom,
  actualityLatestEvents,
  actualityLatestEventsWithIssues,
  actualitySelectedEvent,
  actualityTotalEventsCount,
} from '../store/actuality.hq.state';
import { feedDisplayOptionAtom } from '../store/feed.state';
import { feedDisplayOptions } from '../store/feed.types';
import FeedEventsStats from './FeedEventsStats';

const FeedEventsPanel: React.FC = () => {
  const { t } = useTranslation('feed');

  const eventsAll = useRecoilValue(actualityLatestEvents);
  const eventsWithIssues = useRecoilValue(actualityLatestEventsWithIssues);
  const totalEventsCount = useRecoilValue(actualityTotalEventsCount);
  const [eventsLimit, setEventsLimit] = useRecoilState(
    actualityEventsLimitAtom,
  );
  // getActualityHqConfigBase
  const setSelectedEvent = useSetRecoilState(actualitySelectedEvent);

  const [feedDisplayOption, setFeedDisplayOption] = useRecoilState(
    feedDisplayOptionAtom,
  );

  const [groupingOption, setGroupingOption] = useState('byDay');

  // Determine grouping key based on option
  const getGroupingKey = event => {
    const eventDateEnd = new Date(event.eventEndTime);
    const eventDateStart = new Date(event.eventStartTime ?? null);

    return groupingOption === 'byHour'
      ? `${toHumanReadableDateDay(eventDateEnd)} (${eventDateStart.getHours()}:00 – ${eventDateEnd.getHours()}:00)`
      : toHumanReadableDateDay(eventDateEnd);
  };

  // Filter events based on feedDisplayOption
  const filteredEvents = useMemo(() => {
    if (feedDisplayOption === 'issues') {
      return eventsWithIssues;
    }
    return eventsAll;
  }, [eventsAll, eventsWithIssues, feedDisplayOption]);

  // Group filtered events
  const groupedEvents = useMemo(() => {
    return _(filteredEvents)
      .groupBy(event => getGroupingKey(event))
      .map((events, key) => ({
        groupKey: key,
        events: _.orderBy(
          events,
          ['eventEndTime', 'eventStartTime', 'agentId'],
          ['desc', 'desc', 'asc'],
        ),
      }))
      .value();
  }, [filteredEvents, groupingOption, getGroupingKey]);

  // Prepare events for Virtuoso
  const eventsForVirtuoso = useMemo(() => {
    return groupedEvents.reduce((acc, group) => {
      acc.push({
        type: 'header',
        content: group.groupKey,
        events: group.events,
      });
      return acc;
    }, []);
  }, [groupedEvents]);

  console.log('groupedEvents', groupedEvents);
  console.log('eventsForVirtuoso', eventsForVirtuoso);
  console.log('filteredEvents length', filteredEvents?.length);
  // Calculate if there are multiple unique days or hours for the collapsible condition
  const hasMultipleTimeGroups = useMemo(
    () => groupedEvents.length > 1,
    [groupedEvents],
  );

  return (
    <PanelBody data-component="FeedEventsPanel" className="h-full pb-2">
      <ActionBar
        sticky
        stickyTop
        className={classNames('px-1 py-2', 'bg-sidebar-header/40', 'my-px')}
      >
        <div className="flex w-full items-center">
          <ButtonSwitchMulti
            autoSize
            className="mx-2 my-1 flex-1 text-xs"
            selectedIndex={feedDisplayOptions.findIndex(
              option => option.value === feedDisplayOption,
            )}
            onClick={index =>
              setFeedDisplayOption(feedDisplayOptions[index].value)
            }
            options={feedDisplayOptions}
            buttonType="header"
          />

          {/* Events Limit Control */}
          {/* <div className="mr-2 flex items-center gap-2">
            <select
              value={eventsLimit}
              onChange={e => setEventsLimit(Number(e.target.value))}
              className="rounded  px-2 py-1 text-xs"
            >
              <option value={100}>100</option>
              <option value={500}>500</option>
              <option value={1000}>1,000</option>
              <option value={2000}>2,000</option>
              <option value={5000}>5,000</option>
              <option value={10000}>10,000</option>
            </select>
            <span className="whitespace-nowrap text-xs text-gray-600">
              {t`events`}
            </span>
          </div> */}
        </div>
      </ActionBar>

      {feedDisplayOption === 'stats' ? (
        <FeedEventsStats />
      ) : _.isEmpty(eventsForVirtuoso) ? (
        <InboxZero selfCenter message={t`No events`} />
      ) : (
        <>
          {/* Filtering Message */}
          {totalEventsCount > 0 &&
            filteredEvents?.length < totalEventsCount && (
              <div className="text-menu-text/70 px-3 py-2 text-xs">
                <div className="flex items-center justify-between">
                  <span>
                    {t('Showing {{count}} of {{total}} total events', {
                      count: filteredEvents?.length || 0,
                      total: totalEventsCount,
                    })}
                    {eventsLimit < totalEventsCount && (
                      <span className="ml-2">
                        {t('limited to {{count}} most recent', {
                          count: eventsLimit,
                        })}
                      </span>
                    )}
                  </span>
                  {eventsLimit < totalEventsCount && (
                    <button
                      onClick={() =>
                        setEventsLimit(
                          Math.min(eventsLimit * 2, totalEventsCount),
                        )
                      }
                      className="text-menu-text bg-menu/30 hover:bg-menu/80 border-menu/60 mr-2 rounded border px-2 py-1 text-xs"
                    >
                      {t`Show More`}
                    </button>
                  )}
                </div>
              </div>
            )}
          <Virtuoso
            className={classNames('h-full overflow-x-hidden')}
            data={eventsForVirtuoso}
            itemContent={(index, item) => {
              if (item.type === 'header') {
                return (
                  <TitleSection
                    id={`header-event-${item.content}-${index}`}
                    key={`header-event-${item.content}-${index}`}
                    title={`${item.content}`}
                    titleSummaryClosed={
                      <span className="border-menu/60 text-menu-text text-xxs mr-2 rounded border p-1 px-2 text-end">
                        {item.events.length}
                      </span>
                    }
                    titleSummaryOpen={
                      <span className="border-menu/60 text-menu-text text-xxs mr-2 rounded border p-1 px-2 text-end">
                        {item.events.length}
                      </span>
                    }
                    inSidebarView
                    isSticky
                    className="!z-60 !top-0"
                    fullWidth
                    inPanelView
                    collapsible={true}
                    // collapsible={hasMultipleTimeGroups}
                  >
                    {item.events.map((evt, eventIndex) => (
                      <FeedActivityEventDetails
                        key={`feed-event-details-${evt.eventId}`}
                        event={evt}
                        index={eventIndex}
                        onClick={() => setSelectedEvent(evt)}
                      />
                    ))}
                  </TitleSection>
                );
              }
              return null; // Add explicit return for non-header items
            }}
            components={{
              List: React.forwardRef((props, ref) => (
                <div data-component="event-comps" ref={ref} {...props} />
              )),
            }}
          />
        </>
      )}
    </PanelBody>
  );
};

export default FeedEventsPanel;
